import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Download, Trash2, AlertCircle, MinusCircle } from 'lucide-react';
import api, { assetUrl, API_BASE } from '../../services/api';
import { useConfirmation } from '../../context/ConfirmationContext';

export default function SubjectDetail() {
  const { showConfirmation } = useConfirmation();
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [resources, setResources] = useState([]);
  const [exams, setExams] = useState([]);
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);

  // File upload form state
  const [files, setFiles] = useState([]);
  const [uploadHeadings, setUploadHeadings] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [lastUploadResponse, setLastUploadResponse] = useState(null);

  useEffect(() => {
    loadData();
  }, [subjectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/subjects/${subjectId}/resources`);
      if (res.data.success) {
        setSubject(res.data.subject);
        setResources(res.data.resources || []);
        setExams(res.data.exams || []);
        setLecturers(res.data.lecturers || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load subject');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (index, file) => {
    if (!file) return;
    
    // Check file size (2MB = 2097152 bytes)
    if (file.size > 2097152) {
      setUploadError(`File "${file.name}" exceeds 2MB limit`);
      return;
    }

    // Check file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Only PDF and Word documents are allowed');
      return;
    }

    setUploadError('');
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
  };

  const handleHeadingChange = (index, heading) => {
    const newHeadings = [...uploadHeadings];
    newHeadings[index] = heading;
    setUploadHeadings(newHeadings);
  };

  const addFileSlot = () => {
    setFiles([...files, null]);
    setUploadHeadings([...uploadHeadings, '']);
  };

  const removeFileSlot = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    setUploadHeadings(uploadHeadings.filter((_, i) => i !== index));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError('');

    // Validate
    const validFiles = files.filter(f => f !== null);
    if (validFiles.length === 0) {
      setUploadError('Please select at least one file');
      return;
    }

    if (validFiles.some((f, i) => !uploadHeadings[files.indexOf(f)]?.trim())) {
      setUploadError('Please provide a heading for each file');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      setLastUploadResponse(null);

      const formData = new FormData();
      files.forEach((file, i) => {
        if (file) {
          formData.append('files[]', file);
          formData.append('headings[]', uploadHeadings[i]);
        }
      });

      // Build upload URL based on API_BASE
      const base = API_BASE.replace(/\/$/, '');
      const url = `${base}/admin/subjects/${subjectId}/resources`;

      // Use fetch to ensure multipart is sent correctly and include Authorization explicitly
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'same-origin'
      });

      const data = await response.json().catch(() => ({ error: 'Invalid JSON response' }));
      setLastUploadResponse({ status: response.status, body: data });

      if (!response.ok) {
        const msg = data?.error || data?.message || 'Failed to upload resources';
        setUploadError(msg);
        return;
      }

      // Success
      setSuccess(data.message || 'Resources uploaded successfully!');
      setFiles([]);
      setUploadHeadings([]);
      setShowUploadForm(false);
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      setUploadError(err.message || 'Failed to upload resources');
      setLastUploadResponse({ error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const deleteResource = async (resourceId, resourceName) => {
    showConfirmation({
      title: 'Delete Resource?',
      message: `Are you sure you want to delete "${resourceName}"?\n\nThis action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/admin/subjects/${subjectId}/resources/${resourceId}`);
          if (res.data.success) {
            setSuccess('Resource deleted successfully!');
            setTimeout(() => setSuccess(''), 3000);
            loadData();
          }
        } catch (err) {
          setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete resource');
        }
      }
    });
  };

  const deleteExam = async (examId, examName) => {
    showConfirmation({
      title: 'Delete Exam?',
      message: `Are you sure you want to delete exam "${examName}"? This will remove marks and components for this exam.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/admin/exams/${examId}`);
          if (res.data.success) {
            setSuccess('Exam deleted successfully');
            setTimeout(() => setSuccess(''), 3000);
            loadData();
          }
        } catch (err) {
          setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete exam');
        }
      }
    });
  };

  const unassignLecturer = async (lecturerId, lecturerName) => {
    showConfirmation({
      title: 'Unassign Lecturer?',
      message: `Remove "${lecturerName}" from this subject? This action cannot be undone.`,
      confirmText: 'Unassign',
      cancelText: 'Cancel',
      type: 'warning',
      onConfirm: async () => {
        try {
          const res = await api.delete(`/admin/subjects/${subjectId}/lecturers/${lecturerId}`);
          if (res.data.success) {
            setSuccess('Lecturer unassigned successfully');
            setTimeout(() => setSuccess(''), 3000);
            loadData();
          }
        } catch (err) {
          setError(err.response?.data?.error || err.response?.data?.message || 'Failed to unassign lecturer');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !subject) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/subjects')}
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!subject) return <div>Subject not found</div>;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/subjects')}
        className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Subjects
      </button>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
          {success}
        </div>
      )}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {uploadError}
        </div>
      )}

      {/* Subject Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{subject.subject_name}</h1>
          <p className="text-indigo-600 font-medium mt-1">{subject.subject_code}</p>
          {subject.description && <p className="text-gray-600 mt-3">{subject.description}</p>}
        </div>
        <div>
          <button onClick={() => showConfirmation({
            title: 'Delete Subject?',
            message: `Are you sure you want to delete "${subject.subject_name}"? This action cannot be undone.`,
            type: 'danger',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
              try {
                const res = await api.delete(`/admin/subjects/${subjectId}`);
                if (res.data.success) {
                  setSuccess('Subject deleted successfully');
                  setTimeout(() => navigate('/admin/subjects'), 800);
                }
              } catch (err) {
                setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete subject');
              }
            }
          })} className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition">
            <Trash2 className="w-4 h-4" /> Delete Subject
          </button>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">Subject Resources</h2>
          {!showUploadForm && (
            <button
              onClick={() => setShowUploadForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <Upload className="w-4 h-4" /> Add Resources
            </button>
          )}
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <form onSubmit={handleUpload} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
            <div className="space-y-3">
              {files.map((file, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      placeholder="Resource heading/title"
                      value={uploadHeadings[index] || ''}
                      onChange={(e) => handleHeadingChange(index, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => handleFileSelect(index, e.target.files?.[0])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      {file && <span className="text-sm text-green-600 mt-1">✓ {file.name}</span>}
                      <p className="text-xs text-gray-500 mt-1">Max 2MB • PDF or Word</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFileSlot(index)}
                    className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition mt-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {files.length === 0 && (
              <button
                type="button"
                onClick={addFileSlot}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition"
              >
                + Add File
              </button>
            )}

            {files.length > 0 && (
              <button
                type="button"
                onClick={addFileSlot}
                className="w-full py-2 px-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                + Add Another File
              </button>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowUploadForm(false);
                  setFiles([]);
                  setUploadHeadings([]);
                  setUploadError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || files.filter(f => f !== null).length === 0}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                {uploading ? 'Uploading...' : 'Upload Resources'}
              </button>
            </div>
          </form>
        )}

        {/* Resources List */}
        {resources.length > 0 ? (
          <div className="space-y-3">
            {resources.map((resource) => (
              <div
                key={resource.resource_id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">{resource.resource_heading}</h4>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                    <span>📄 {resource.file_name}</span>
                    <span>({(resource.file_size / 1024).toFixed(2)} KB)</span>
                    <span>Added {new Date(resource.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={assetUrl(resource.file_path)}
                    download={resource.file_name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition"
                    title="Download"
                  >
                    <Download className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => deleteResource(resource.resource_id, resource.resource_heading)}
                    className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400">No resources uploaded yet</p>
          </div>
        )}
      </div>

      {/* Exams Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">Exams</h2>
        </div>

        {exams.length > 0 ? (
          <div className="space-y-3">
            {exams.map((exam) => (
              <div key={exam.exam_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                <div>
                  <h4 className="font-medium text-gray-800">{exam.exam_name}</h4>
                  <p className="text-sm text-gray-500">Date: {exam.exam_date || 'TBA'} • Total: {exam.total_marks}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => deleteExam(exam.exam_id, exam.exam_name)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition" title="Delete Exam">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400">No exams for this subject</p>
          </div>
        )}
      </div>

      {/* Assigned Lecturers */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Assigned Lecturers</h2>
        {lecturers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lecturers.map((l) => (
              <div key={l.user_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-800">{l.full_name}</div>
                  <div className="text-sm text-gray-500">{l.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => unassignLecturer(l.user_id, l.full_name)} className="p-2 hover:bg-yellow-50 text-yellow-700 rounded-lg transition" title="Unassign Lecturer">
                    <MinusCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400">No lecturers assigned to this subject</div>
        )}
      </div>
    </div>
  );
}
